import { Icon, type IconName } from '@/shared/components/ui/Icon';
import { THEME_MODES, type ThemeMode } from './applyTheme';
import { useTheme } from './useTheme';
import { useI18n } from '@/shared/i18n/useT';

interface ThemeSwitchProps {
  className?: string;
  disabled?: boolean;
}

const MODE_ICONS: Record<ThemeMode, IconName> = {
  light: 'sun',
  dark: 'moon',
  system: 'monitor',
};

/**
 * Reusable pill-style theme mode switcher (`Light` | `Dark` | `Auto`).
 * Used in TopBar settings dropdown, GridColorsSection, and Preferences.
 * Always renders full text without truncation.
 */
export function ThemeSwitch({ className, disabled }: ThemeSwitchProps) {
  const { mode, setMode } = useTheme();
  const { t } = useI18n();

  return (
    <div
      className={`flex rounded-full border border-border bg-surface-2 p-1 ${
        className ?? 'w-full'
      }`}
      role="radiogroup"
      aria-label={t('settings.theme.label')}
    >
      {THEME_MODES.map((themeMode) => {
        const activo = mode === themeMode;
        return (
          <button
            key={themeMode}
            type="button"
            role="radio"
            aria-checked={activo}
            disabled={disabled}
            aria-label={t(`settings.theme.${themeMode}`)}
            onClick={() => setMode(themeMode)}
            className={`flex flex-1 shrink-0 items-center justify-center gap-1.5 rounded-full py-1.5 px-2 text-xs font-semibold transition-all disabled:opacity-60 ${
              activo
                ? 'bg-surface text-accent shadow-sm'
                : 'text-muted hover:text-fg'
            }`}
          >
            <Icon name={MODE_ICONS[themeMode]} className="h-3.5 w-3.5 shrink-0" />
            <span className="whitespace-nowrap">{t(`settings.theme.${themeMode}`)}</span>
          </button>
        );
      })}
    </div>
  );
}
